import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getUserSettings, updateUserSettings } from "../utils/api";

const DashboardLiveContext = createContext(null);

export function DashboardLiveProvider({ children }) {
  const [isLiveEnabled, setIsLiveEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [liveVersion, setLiveVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await getUserSettings();
        if (!cancelled) {
          setIsLiveEnabled(Boolean(data?.settings?.dashboard_live_enabled));
        }
      } catch (err) {
        console.error("[DashboardLive] Failed to load settings:", err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Access Control (and other tabs) can push the same user_settings column live
  useEffect(() => {
    const onRemoteChange = (event) => {
      if (typeof event?.detail?.dashboard_live_enabled !== "boolean") {
        return;
      }
      setIsLiveEnabled(event.detail.dashboard_live_enabled);
      setLiveVersion((version) => version + 1);
    };
    window.addEventListener("dashboard-live-settings-changed", onRemoteChange);
    return () => {
      window.removeEventListener("dashboard-live-settings-changed", onRemoteChange);
    };
  }, []);

  const setLiveEnabled = useCallback(async (enabled) => {
    const next = Boolean(enabled);
    setIsSaving(true);
    try {
      const data = await updateUserSettings({ dashboard_live_enabled: next });
      const saved = Boolean(data?.settings?.dashboard_live_enabled);
      setIsLiveEnabled(saved);
      setLiveVersion((version) => version + 1);
      return saved;
    } catch (err) {
      console.error("[DashboardLive] Failed to update settings:", err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const value = useMemo(
    () => ({
      isLiveEnabled,
      isLoading,
      isSaving,
      setLiveEnabled,
      liveVersion,
    }),
    [isLiveEnabled, isLoading, isSaving, setLiveEnabled, liveVersion],
  );

  return (
    <DashboardLiveContext.Provider value={value}>
      {children}
    </DashboardLiveContext.Provider>
  );
}

export function useDashboardLive() {
  const context = useContext(DashboardLiveContext);
  if (!context) {
    throw new Error("useDashboardLive must be used within DashboardLiveProvider");
  }
  return context;
}
